/**
 * AI Compliance Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * EU AI Act compliance and advice restrictions.
 */

import React from 'react';
import { AlertTriangle, Scale, FileText, Info } from 'lucide-react';
import type { AISettingsUpdateData } from '../../../types/admin';

interface AIComplianceSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const ADVICE_TYPES = [
  {
    key: 'investment_advice_allowed' as const,
    label: 'Investment Advice',
    description: 'Allow AI to provide specific investment recommendations',
    warning:
      'Requires MiFID II compliance and proper licensing. Most jurisdictions require human oversight for investment advice.',
    icon: Scale,
  },
  {
    key: 'tax_advice_allowed' as const,
    label: 'Tax Advice',
    description: 'Allow AI to provide specific tax guidance',
    warning:
      'Tax advice typically requires professional qualifications. AI may provide general information only.',
    icon: FileText,
  },
  {
    key: 'legal_advice_allowed' as const,
    label: 'Legal Advice',
    description: 'Allow AI to provide legal guidance',
    warning:
      'Legal advice is restricted in most jurisdictions. AI should not practice law without proper licensing.',
    icon: Scale,
  },
];

export const AIComplianceSettings: React.FC<AIComplianceSettingsProps> = ({
  formData,
  onChange,
}) => {
  const requireDisclaimer = formData.require_disclaimer ?? true;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Compliance Settings</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure advice restrictions and compliance requirements per EU AI Act and industry
          regulations.
        </p>
      </div>

      {/* EU AI Act Toggles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
          <div>
            <span className="font-medium text-gray-900">EU AI Act Compliant</span>
            <p className="text-sm text-gray-500">Enable EU AI Act guardrails</p>
          </div>
          <input
            type="checkbox"
            checked={formData.eu_ai_act_compliant ?? true}
            onChange={(e) => onChange({ eu_ai_act_compliant: e.target.checked })}
            className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
          />
        </label>

        <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300">
          <div>
            <span className="font-medium text-gray-900">GDPR Compliant</span>
            <p className="text-sm text-gray-500">Enable GDPR data protection</p>
          </div>
          <input
            type="checkbox"
            checked={formData.gdpr_compliant ?? true}
            onChange={(e) => onChange({ gdpr_compliant: e.target.checked })}
            className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
          />
        </label>
      </div>

      {/* Advice Restrictions */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Advice Restrictions</h4>
        <p className="text-sm text-gray-500 mb-4">
          By default, the AI is restricted from providing professional advice that requires
          licensing. Enable with caution.
        </p>

        <div className="space-y-4">
          {ADVICE_TYPES.map((advice) => {
            const Icon = advice.icon;
            const isEnabled = formData[advice.key] ?? false;

            return (
              <div
                key={advice.key}
                className={`
                  p-4 rounded-lg border-2 transition-colors
                  ${isEnabled ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}
                `}
              >
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${isEnabled ? 'bg-amber-100' : 'bg-gray-100'}`}
                    >
                      <Icon size={18} className={isEnabled ? 'text-amber-600' : 'text-gray-400'} />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">{advice.label}</span>
                      <p className="text-sm text-gray-500">{advice.description}</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => onChange({ [advice.key]: e.target.checked })}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                </label>
                {isEnabled && (
                  <div className="mt-3 ml-11 p-3 bg-amber-100 rounded border border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{advice.warning}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Disclaimer Settings */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Disclaimer Requirements</h4>

        <label className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 mb-4">
          <div>
            <span className="font-medium text-gray-900">Require Disclaimers</span>
            <p className="text-sm text-gray-500">
              Show disclaimers when AI provides financial, tax, or legal information
            </p>
          </div>
          <input
            type="checkbox"
            checked={requireDisclaimer}
            onChange={(e) => onChange({ require_disclaimer: e.target.checked })}
            className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
          />
        </label>

        {requireDisclaimer && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Disclaimer Text
            </label>
            <textarea
              value={formData.disclaimer_text || ''}
              onChange={(e) => onChange({ disclaimer_text: e.target.value || undefined })}
              placeholder="Leave empty for default disclaimer..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default: "This is AI-generated content for informational purposes only. Please verify
              important information with qualified professionals."
            </p>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">EU AI Act Requirements</p>
            <ul className="mt-1 list-disc list-inside text-xs">
              <li>High-risk AI systems require human oversight</li>
              <li>Users must be informed they are interacting with AI</li>
              <li>AI-generated content in regulated domains requires transparency</li>
              <li>Full enforcement begins August 2026</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIComplianceSettings;
